/**
 * usePaginatedList — shared ViewModel hook for every paginated list
 * endpoint. Per SOP 02 §"List Pagination (LOCKED — 2026-05-13)" +
 * §"Mutation Patterns (LOCKED — 2026-05-18)".
 *
 * Composes:
 *   - page state (1-indexed)
 *   - filter-deps → auto reset to page 1 BEFORE refetch
 *   - loading + error tracking
 *   - extras: any catalogue payload the fetcher returns alongside
 *     items + total (e.g. screens catalogue rides in /iam/user-types)
 *
 * MUTATION PRIMITIVES (added 2026-05-18) — viewmodels MUST use these
 * after a successful PATCH / POST / DELETE instead of calling
 * `reload()`. The handler returns the new/updated DTO; we splice it
 * locally and skip a second round-trip:
 *   - updateItem(matcher, dto) — after PATCH
 *   - insertItem(dto)          — after POST
 *   - removeItem(matcher)      — after DELETE (auto-steps back one
 *                                page if the deletion empties page N>1)
 *
 * SEQUENCE GUARD (added 2026-05-18) — if a mutation happens while a
 * reload is in flight, the reload's result is DISCARDED on arrival so
 * the mutation's local update isn't clobbered. Implementation: every
 * mutation bumps a generation counter; reload captures the counter at
 * start and only applies if it hasn't moved. Known trade-off: a
 * filter-change reload concurrent with a mutation will also be
 * discarded — the next user action triggers a fresh fetch. Rare in
 * single-operator workflows; documented limit.
 *
 * Caller responsibility: the `fetcher` builds the URL with the page +
 * pageSize the hook hands it, calls authedFetch, and returns
 * { items, total, extras? }. The hook never knows the API path.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { PAGINATION } from '../constants/api.constants'
import { isNetworkError, SERVER_UNAVAILABLE_MESSAGE } from '../utils/api/server-unavailable'

export interface PaginatedFetchResult<T, X = undefined> {
  readonly items: T[]
  readonly total: number
  /** Catalogue / metadata that rides alongside paginated items. */
  readonly extras?: X
}

export interface UsePaginatedListInput<T, X = undefined> {
  /**
   * Called every time the hook needs data. Receives the page number
   * (1-indexed), pageSize, and an AbortSignal so the caller can cancel
   * a stale request. Returns the unwrapped data shape.
   */
  readonly fetcher: (
    page: number,
    pageSize: number,
    signal: AbortSignal
  ) => Promise<PaginatedFetchResult<T, X>>
  /** Page size for every fetch. Defaults to PAGINATION.PAGESIZEDEFAULT. */
  readonly pageSize?: number
  /**
   * Filters / sort keys that should reset page to 1 when they change.
   * Pass `[]` (or omit) for endpoints with no filters yet.
   */
  readonly deps?: ReadonlyArray<unknown>
}

export interface UsePaginatedListReturn<T, X = undefined> {
  readonly items: T[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly extras: X | undefined
  readonly setPage: (n: number) => void
  readonly reload: () => void
  /**
   * Splice an updated row into the local list in place. Use after a
   * successful PATCH with the DTO the server returned. The matcher
   * picks the row by some stable key (usually id).
   */
  readonly updateItem: (matcher: (item: T) => boolean, replacement: T) => void
  /**
   * Prepend a new row to the local list. Use after a successful POST
   * when the user is on page 1 (the most common case — new rows are
   * normally surfaced via DESC created_at). If page > 1 OR if the
   * list's sort would place the new row outside the current page,
   * the caller should `reload()` instead. Bumps `total` by 1.
   */
  readonly insertItem: (item: T) => void
  /**
   * Filter a row out of the local list. Use after a successful
   * DELETE. Auto-steps back one page when the deletion empties the
   * current page AND page > 1, so the operator never sees a blank.
   * Bumps `total` down by 1.
   */
  readonly removeItem: (matcher: (item: T) => boolean) => void
}

export function usePaginatedList<T, X = undefined>(
  input: UsePaginatedListInput<T, X>
): UsePaginatedListReturn<T, X> {
  const pageSize = input.pageSize ?? PAGINATION.PAGESIZEDEFAULT
  const deps = input.deps ?? []
  const [page, setPage] = useState<number>(PAGINATION.PAGEDEFAULT)
  const [items, setItems] = useState<T[]>([])
  const [total, setTotal] = useState<number>(0)
  const [extras, setExtras] = useState<X | undefined>(undefined)
  /**
   * Spinner shows only on the FIRST load. Subsequent reloads (after a
   * create/update/delete) keep the previous rows visible while the new
   * data is in flight — no full-screen flash.
   */
  const [hasLoadedOnce, setHasLoadedOnce] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  /** Bump to force a manual reload. */
  const [reloadKey, setReloadKey] = useState<number>(0)
  /** Snapshot of the fetcher to avoid stale closures. */
  const fetcherRef = useRef(input.fetcher)
  fetcherRef.current = input.fetcher
  /**
   * Mutation generation counter — every local-state mutation
   * (updateItem / insertItem / removeItem) bumps it. Reload effects
   * capture the value at fetch-start time and only apply the result
   * if the counter is unchanged on return. Sequence guard against
   * stale reloads clobbering local patches.
   */
  const mutationGenRef = useRef(0)

  // Filter change → reset page to 1 BEFORE the fetch effect runs.
  // We use a separate effect that fires on deps change to keep the
  // "page resets to 1" semantic deterministic.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setPage(PAGINATION.PAGEDEFAULT)
  }, deps)

  // The fetch effect — fires when page, pageSize, deps, or reloadKey change.
  useEffect(() => {
    const ac = new AbortController()
    let cancelled = false
    const genAtStart = mutationGenRef.current
    // Spinner only on the very first fetch. After that, keep the
    // existing rows on screen while the new data flies in.
    if (!hasLoadedOnce) setIsLoading(true)
    setError(null)
    fetcherRef
      .current(page, pageSize, ac.signal)
      .then((res) => {
        if (cancelled) return
        // Sequence guard: if any mutation happened while this fetch was
        // in flight, discard the result. The mutation already applied
        // locally; the next user-triggered action will fetch fresh.
        if (mutationGenRef.current !== genAtStart) return
        setItems(res.items)
        setTotal(res.total)
        // Only set extras when the fetcher actually returned a value.
        // This lets callers split slow-changing catalogues into a
        // separate one-shot fetch so reloads don't re-pull them.
        if (res.extras !== undefined) setExtras(res.extras)
        setHasLoadedOnce(true)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        if (err instanceof DOMException && err.name === 'AbortError') return
        // Browser network throw (DNS/CORS/offline/gateway unreachable)
        // surfaces as TypeError — map to the generic infrastructure
        // message; any other error already carries its own string.
        if (isNetworkError(err)) {
          setError(SERVER_UNAVAILABLE_MESSAGE)
        } else {
          setError(err instanceof Error ? err.message : SERVER_UNAVAILABLE_MESSAGE)
        }
        setItems([])
        setTotal(0)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
      ac.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, reloadKey, ...deps])

  const reload = useCallback((): void => {
    setReloadKey((k) => k + 1)
  }, [])

  const updateItem = useCallback(
    (matcher: (item: T) => boolean, replacement: T): void => {
      mutationGenRef.current += 1
      setItems((prev) => prev.map((it) => (matcher(it) ? replacement : it)))
    },
    []
  )

  const insertItem = useCallback((item: T): void => {
    mutationGenRef.current += 1
    setItems((prev) => [item, ...prev])
    setTotal((n) => n + 1)
  }, [])

  const removeItem = useCallback(
    (matcher: (item: T) => boolean): void => {
      mutationGenRef.current += 1
      setItems((prev) => {
        const next = prev.filter((it) => !matcher(it))
        // Auto-step-back: if we just emptied a page beyond the first,
        // ask for the previous page so the operator doesn't see blank.
        // setPage triggers a reload via the fetch effect's deps; that
        // reload will arrive with fresh items + accurate total.
        if (next.length === 0 && page > 1) {
          // Defer so the state batch flushes the items update first.
          queueMicrotask(() => setPage((p) => Math.max(1, p - 1)))
        }
        return next
      })
      setTotal((n) => Math.max(0, n - 1))
    },
    [page]
  )

  return {
    items,
    total,
    page,
    pageSize,
    isLoading,
    error,
    extras,
    setPage,
    reload,
    updateItem,
    insertItem,
    removeItem
  }
}
